import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { ShoppingBag, Plus, Search, Check, Clock, UserPlus, Settings, Trash2, Eye, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSpecialLists = () => {
  const [tab, setTab] = useState("customers"); // "customers" | "lists"
  const [specialCustomers, setSpecialCustomers] = useState([]);
  const [lists, setLists] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form nuovo cliente speciale
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", password: "", max_order: 15, notification_message: "" });

  // Form modifica config
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editConfig, setEditConfig] = useState({ max_order_amount: 15, notification_message: "", free_shipping: true, no_points: true });

  // Form crea lista
  const [showCreateList, setShowCreateList] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [listItems, setListItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Dettaglio lista
  const [viewingList, setViewingList] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const h = { Authorization: `Bearer ${token}` };
      const [listsRes, custRes, prodRes] = await Promise.all([
        axios.get(`${API}/admin/special-lists`, { headers: h }),
        axios.get(`${API}/admin/customers`, { headers: h }),
        axios.get(`${API}/products?admin_view=true`)
      ]);
      setLists(listsRes.data || []);
      setSpecialCustomers((custRes.data || []).filter(c => c.is_special_customer));
      setProducts(prodRes.data.filter(p => p.active && !p.business_only));
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };

  // === GESTIONE CLIENTI SPECIALI ===
  const handleCreateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email || !newCustomer.password) {
      toast.error("Compila nome, email e password"); return;
    }
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/special-customers`, newCustomer, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cliente speciale creato!");
      setShowNewCustomer(false);
      setNewCustomer({ name: "", email: "", password: "", max_order: 15, notification_message: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore creazione");
    }
  };

  const handleUpdateConfig = async () => {
    if (!editingCustomer) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/special-customers/${editingCustomer.id}`, editConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurazione aggiornata!");
      setEditingCustomer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm("Eliminare questa lista? I prodotti NON verranno rimessi in magazzino.")) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/special-lists/${listId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lista eliminata");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore eliminazione");
    }
  };

  const handleRemoveSpecialStatus = async (customerId) => {
    if (!window.confirm("Rimuovere lo stato di cliente speciale? L'account resterà come cliente normale.")) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/special-customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status speciale rimosso");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  // === GESTIONE LISTA ===
  const addToList = (product) => {
    const existing = listItems.find(i => i.product_id === product.id);
    if (existing) {
      setListItems(listItems.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setListItems([...listItems, { product_id: product.id, name: product.name, price: product.price, quantity: 1 }]);
    }
  };

  const updateListQty = (productId, qty) => {
    if (qty <= 0) setListItems(listItems.filter(i => i.product_id !== productId));
    else setListItems(listItems.map(i => i.product_id === productId ? { ...i, quantity: qty } : i));
  };

  const handleCreateList = async () => {
    if (!selectedCustomer) { toast.error("Seleziona un cliente"); return; }
    if (listItems.length === 0) { toast.error("Aggiungi prodotti"); return; }
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/special-list/${selectedCustomer}`, { items: listItems }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lista creata!");
      setShowCreateList(false);
      setListItems([]);
      setSelectedCustomer("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore creazione lista");
    }
  };

  const filteredProducts = products.filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const listTotal = listItems.reduce((s, i) => s + i.price * i.quantity, 0);

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center h-64"><div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-special-lists">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <ShoppingBag className="w-8 h-8 text-yellow-500" />
            Clienti Speciali
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <button onClick={() => setTab("customers")} className={`px-4 py-2 rounded-lg font-semibold text-sm ${tab === "customers" ? "bg-yellow-500 text-black" : "bg-zinc-800 text-zinc-400"}`}>
            Clienti ({specialCustomers.length})
          </button>
          <button onClick={() => setTab("lists")} className={`px-4 py-2 rounded-lg font-semibold text-sm ${tab === "lists" ? "bg-yellow-500 text-black" : "bg-zinc-800 text-zinc-400"}`}>
            Liste ({lists.length})
          </button>
        </div>

        {/* ========= TAB CLIENTI ========= */}
        {tab === "customers" && (
          <div className="space-y-4">
            <button onClick={() => setShowNewCustomer(!showNewCustomer)} className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-bold hover:bg-yellow-600 flex items-center gap-2">
              <UserPlus className="w-5 h-5" /> Nuovo Cliente Speciale
            </button>

            {/* Form nuovo cliente */}
            {showNewCustomer && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-3">
                <h3 className="text-white font-bold">Nuovo Cliente Speciale</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-zinc-400 text-xs">Nome completo *</label>
                    <input type="text" value={newCustomer.name} onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs">Email *</label>
                    <input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs">Password *</label>
                    <input type="text" value={newCustomer.password} onChange={(e) => setNewCustomer({...newCustomer, password: e.target.value})}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs">Max ordine (€)</label>
                    <input type="number" step="0.5" value={newCustomer.max_order} onChange={(e) => setNewCustomer({...newCustomer, max_order: parseFloat(e.target.value)})}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs">Messaggio notifica (apparirà nelle mail/WhatsApp)</label>
                  <input type="text" value={newCustomer.notification_message} onChange={(e) => setNewCustomer({...newCustomer, notification_message: e.target.value})}
                    placeholder="Es: CLIENTE NOME - Emettere scontrino come da accordi"
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCreateCustomer} className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-bold hover:bg-yellow-600">Crea</button>
                  <button onClick={() => setShowNewCustomer(false)} className="border border-zinc-700 text-zinc-400 px-4 py-2 rounded-lg">Annulla</button>
                </div>
              </div>
            )}

            {/* Lista clienti */}
            {specialCustomers.length === 0 ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 text-center">
                <p className="text-zinc-400">Nessun cliente speciale</p>
              </div>
            ) : (
              specialCustomers.map(c => {
                const config = c.special_config || {};
                const activeList = lists.find(l => l.customer_id === c.id && !l.completed);
                return (
                  <div key={c.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-bold text-lg">{c.name}</h3>
                        <p className="text-zinc-500 text-sm">{c.email}</p>
                        <div className="flex gap-3 mt-2 text-xs">
                          <span className="bg-yellow-500/20 text-yellow-500 px-2 py-1 rounded">Max €{config.max_order_amount || 15}</span>
                          {config.free_shipping && <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded">Spedizione gratis</span>}
                          {config.no_points && <span className="bg-zinc-700 text-zinc-400 px-2 py-1 rounded">No punti</span>}
                          {activeList && <span className="bg-blue-500/20 text-blue-500 px-2 py-1 rounded">Lista attiva</span>}
                        </div>
                        {config.notification_message && <p className="text-zinc-600 text-xs mt-1 italic">{config.notification_message}</p>}
                      </div>
                      <div className="flex gap-2">
                        {activeList && (
                          <button onClick={async () => {
                            if (!window.confirm(`Azzerare la lista attiva di ${c.name}?`)) return;
                            try {
                              const token = localStorage.getItem("admin_token");
                              await axios.delete(`${API}/admin/special-lists/${activeList.id}`, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success("Lista azzerata");
                              fetchData();
                            } catch { toast.error("Errore"); }
                          }} className="text-yellow-500 hover:text-yellow-400 p-2" title="Azzera lista">
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                        <button onClick={() => { setEditingCustomer(c); setEditConfig({ email: c.email, new_password: "", max_order_amount: config.max_order_amount || 15, notification_message: config.notification_message || "", free_shipping: config.free_shipping !== false, no_points: config.no_points !== false }); }}
                          className="text-zinc-400 hover:text-white p-2" title="Modifica"><Settings className="w-5 h-5" /></button>
                        <button onClick={() => handleRemoveSpecialStatus(c.id)}
                          className="text-red-400 hover:text-red-300 p-2" title="Rimuovi status speciale"><Trash2 className="w-5 h-5" /></button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Modale modifica config */}
            {editingCustomer && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 max-w-md w-full">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold">Modifica {editingCustomer.name}</h3>
                    <button onClick={() => setEditingCustomer(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-zinc-400 text-xs">Email</label>
                      <input type="email" value={editConfig.email} onChange={(e) => setEditConfig({...editConfig, email: e.target.value})}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs">Nuova password (lascia vuoto per non cambiare)</label>
                      <input type="text" value={editConfig.new_password} onChange={(e) => setEditConfig({...editConfig, new_password: e.target.value})}
                        placeholder="••••••••"
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs">Max ordine (€)</label>
                      <input type="number" step="0.5" value={editConfig.max_order_amount} onChange={(e) => setEditConfig({...editConfig, max_order_amount: parseFloat(e.target.value)})}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs">Messaggio notifica</label>
                      <input type="text" value={editConfig.notification_message} onChange={(e) => setEditConfig({...editConfig, notification_message: e.target.value})}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-zinc-300 text-sm">
                        <input type="checkbox" checked={editConfig.free_shipping} onChange={(e) => setEditConfig({...editConfig, free_shipping: e.target.checked})} className="rounded" />
                        Spedizione gratis
                      </label>
                      <label className="flex items-center gap-2 text-zinc-300 text-sm">
                        <input type="checkbox" checked={editConfig.no_points} onChange={(e) => setEditConfig({...editConfig, no_points: e.target.checked})} className="rounded" />
                        No punti
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={handleUpdateConfig} className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-bold hover:bg-yellow-600">Salva</button>
                    <button onClick={() => setEditingCustomer(null)} className="border border-zinc-700 text-zinc-400 px-4 py-2 rounded-lg">Annulla</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========= TAB LISTE ========= */}
        {tab === "lists" && (
          <div className="space-y-4">
            <button onClick={() => setShowCreateList(!showCreateList)} className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-bold hover:bg-yellow-600 flex items-center gap-2">
              <Plus className="w-5 h-5" /> Crea Lista Manuale
            </button>

            {/* Form crea lista */}
            {showCreateList && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                <h3 className="text-white font-bold mb-3">Crea lista per cliente speciale</h3>
                <div className="mb-3">
                  <select value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">Seleziona cliente</option>
                    {specialCustomers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
                  </select>
                </div>
                <div className="grid lg:grid-cols-2 gap-4">
                  <div>
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Cerca..." className="w-full bg-black border border-zinc-700 rounded-lg pl-9 pr-3 py-2 text-white text-sm" />
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {filteredProducts.map(p => (
                        <div key={p.id} className="flex items-center justify-between bg-zinc-800 rounded p-2">
                          <div><p className="text-white text-xs">{p.name}</p><p className="text-green-500 text-xs">€{p.price.toFixed(2)}</p></div>
                          <button onClick={() => addToList(p)} className="bg-green-500 text-black w-6 h-6 rounded-full flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-2">Nella lista ({listItems.length})</h4>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {listItems.map(i => (
                        <div key={i.product_id} className="flex items-center justify-between bg-zinc-800 rounded p-2">
                          <p className="text-white text-xs truncate flex-1">{i.name} (€{(i.price * i.quantity).toFixed(2)})</p>
                          <input type="number" min="0" value={i.quantity} onChange={(e) => updateListQty(i.product_id, parseInt(e.target.value) || 0)}
                            className="w-12 bg-black border border-zinc-700 rounded px-1 py-1 text-white text-xs text-center ml-2" />
                        </div>
                      ))}
                    </div>
                    {listItems.length > 0 && <p className="text-green-500 font-bold text-sm mt-2">Totale: €{listTotal.toFixed(2)}</p>}
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={handleCreateList} className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-bold">Crea Lista</button>
                  <button onClick={() => { setShowCreateList(false); setListItems([]); }} className="border border-zinc-700 text-zinc-400 px-4 py-2 rounded-lg">Annulla</button>
                </div>
              </div>
            )}

            {/* Liste esistenti */}
            {lists.length === 0 ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 text-center">
                <ShoppingBag className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">Nessuna lista creata</p>
              </div>
            ) : (
              lists.map(list => {
                const total = list.items.reduce((s, i) => s + i.price * i.quantity_total, 0);
                const ordered = list.items.reduce((s, i) => s + i.price * i.quantity_ordered, 0);
                const pct = total > 0 ? Math.round((ordered / total) * 100) : 0;

                return (
                  <div key={list.id} className={`bg-zinc-900 border rounded-lg p-4 ${list.completed ? "border-green-500/30" : "border-zinc-800"}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {list.completed ? <Check className="w-5 h-5 text-green-500" /> : <Clock className="w-5 h-5 text-yellow-500" />}
                        <div>
                          <h3 className="text-white font-bold">{list.customer_name}</h3>
                          <p className="text-zinc-500 text-xs">{new Date(list.created_at).toLocaleDateString("it-IT")} | €{ordered.toFixed(2)} / €{total.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold px-3 py-1 rounded-full ${list.completed ? "bg-green-500/20 text-green-500" : "bg-yellow-500/20 text-yellow-500"}`}>
                          {list.completed ? "Completata" : `${pct}%`}
                        </span>
                        <button onClick={() => setViewingList(viewingList?.id === list.id ? null : list)} className="text-zinc-400 hover:text-white p-1"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteList(list.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>

                    <div className="w-full bg-zinc-800 rounded-full h-2 mb-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${pct}%` }}></div>
                    </div>

                    {viewingList?.id === list.id && (
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {list.items.map(item => {
                          const rem = item.quantity_total - item.quantity_ordered;
                          return (
                            <div key={item.product_id} className={`text-xs p-2 rounded ${rem <= 0 ? "bg-green-500/10 text-green-400" : "bg-zinc-800 text-zinc-300"}`}>
                              <p className="font-medium truncate">{item.name}</p>
                              <p>€{item.price.toFixed(2)} | {item.quantity_ordered}/{item.quantity_total} {rem <= 0 && <Check className="w-3 h-3 inline" />}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSpecialLists;
